// Shell bar: brand, the World plate, screen tabs, display name, identity chip and the menu gear.
// Document screens (hub, World lobby, party lobby, aftermath) sit under the full bar; the game
// screens that fill the window (overworld, local map, encounter) hide it (App) and put the same
// pieces — `ShellPlate`, `ShellNav`, `ShellName`, `ShellMenuButton` — into their own top bars, so
// every testid (`shell-world`, `nav-*`, `display-name`, `shell-menu`) exists on every screen, except
// that the tabs and the brand's jump stand down on the guided path (ui/guided.ts).
import { useState } from 'react';
import { HotkeyKbd, IconButton, Input } from '../components';
import { useKeyLabel } from '../../core/input/hotkeys';
import { useRoute, type Screen } from '../../core/stores/route';
import { useSession } from '../../core/stores/session';
import { useMyAccount, useWorld, useWorldClock, useWorldSettings } from '../../core/subscriptions/hooks';
import { rpc } from '../../core/net/reducers';
import { shortId } from '../../core/net/connection';
import { useOverlay } from '../overlay';
import { useGuided } from '../guided';
import { shortDate } from '../../core/story/calendar';
import { lobbyTitle, worldTitle } from '../../core/story/worldName';

/** Screens that draw their own top bar instead of the Shell. */
export const BARLESS: readonly Screen[] = ['world', 'localmap', 'encounter', 'writing', 'intro', 'epilogue', 'chapter', 'village'];

/** The World's name and date (`shell-world`): the storyteller's name, the lobby's on hover (docs/37). */
export function ShellPlate({ className = '' }: { className?: string }) {
  const worldId = useSession((s) => s.worldId);
  const world = useWorld(worldId);
  const settings = useWorldSettings(worldId);
  const clock = useWorldClock(worldId);
  if (!world) return null;
  const lobby = lobbyTitle(settings, world);
  // docs/23–24: the realm's date once the story is told; before that the World has no time yet
  return <span className={`fk-shell__plate ${className}`} data-testid="shell-world" data-lobby-name={lobby} title={`Lobby: ${lobby}`}><span className="fk-shell__plate-name">{worldTitle(world)}</span>{clock && <span className="fk-shell__plate-day" data-testid="shell-date">{shortDate(clock)}</span>}</span>;
}

/** Screen tabs (`nav-*`). */
export function ShellNav({ className = '' }: { className?: string }) {
  const screen = useRoute((s) => s.screen);
  const partyId = useRoute((s) => s.partyId);
  const encounterId = useRoute((s) => s.encounterId);
  const instanceId = useRoute((s) => s.instanceId);
  const go = useRoute((s) => s.go);
  const worldId = useSession((s) => s.worldId);
  const gathering = useWorldSettings(worldId)?.phase === 0;
  const guided = useGuided();
  if (guided) return null;
  const tabs: Array<{ id: Screen; label: string; testId: string; active: boolean; show: boolean }> = [
    { id: 'online', label: 'Play online', testId: 'nav-online', active: screen === 'online' || (screen === 'world' && !worldId), show: true },
    { id: 'gathering', label: 'World lobby', testId: 'nav-gathering', active: screen === 'gathering', show: !!worldId && gathering },
    { id: 'world', label: 'World', testId: 'nav-world', active: screen === 'world' && !!worldId, show: !!worldId && !gathering },
    { id: 'lobby', label: partyId ? `Lobby #${partyId}` : 'Lobby', testId: 'nav-lobby', active: screen === 'lobby', show: !!worldId && !gathering },
    { id: 'localmap', label: 'Local map', testId: 'nav-localmap', active: screen === 'localmap', show: !!worldId && !!instanceId },
    { id: screen === 'aftermath' ? 'aftermath' : 'encounter', label: 'Encounter', testId: 'nav-encounter', active: screen === 'encounter' || screen === 'aftermath', show: !!encounterId },
  ];
  return (
    <nav className={`fk-shell__nav ${className}`} aria-label="Screens">
      {tabs.filter((t) => t.show).map((t) => <button key={t.testId} type="button" className="fk-shell__tab" aria-pressed={t.active} onClick={() => go(t.id)} data-testid={t.testId}>{t.label}</button>)}
    </nav>
  );
}

/** The player's display name in this World (`display-name`). */
export function ShellName({ label = true }: { label?: boolean }) {
  const worldId = useSession((s) => s.worldId);
  const account = useMyAccount().find((m) => m.worldId === worldId);
  const [name, setName] = useState<string | undefined>();
  if (!worldId || !account) return null;
  const display = name ?? account.displayName ?? '';
  const commit = () => { if (name !== undefined && name.trim() && name !== account.displayName) void rpc.setDisplayName(worldId, name.trim()); setName(undefined); };
  return (
    <label className="fk-shell__name">
      {label && <span className="fk-shell__name-label">name</span>}
      <Input small data-testid="display-name" value={display} onChange={(e) => setName(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} aria-label="Display name" />
    </label>
  );
}

/** The ☰ that opens the system menu (`shell-menu`). */
export function ShellMenuButton() {
  const openMenu = useOverlay((s) => s.openMenu);
  const key = useKeyLabel('menu');
  return <IconButton small variant="ghost" label="Menu" title={key ? `Menu (${key})` : 'Menu'} onClick={openMenu} data-testid="shell-menu"><span aria-hidden="true">☰</span></IconButton>;
}

export function Shell() {
  const go = useRoute((s) => s.go);
  const identity = useSession((s) => s.identity ?? '');
  const phase = useSession((s) => s.phase);
  const guided = useGuided();
  return (
    <header className="fk-shell fk-hit" data-testid="shell" data-guided={guided ? 'true' : undefined}>
      <button type="button" className="fk-shell__brand" disabled={guided} onClick={() => go('title')} title={guided ? undefined : 'Title screen'} data-testid="shell-brand"><span className="fk-shell__gem" aria-hidden="true">◆</span>Fellowkeep</button>
      <ShellPlate />
      <ShellNav />
      <span className="fk-spacer" />
      <ShellName />
      <span className="fk-shell__id" title={identity} data-testid="identity">{identity ? shortId(identity) : phase}</span>
      <ShellMenuButton />
      <span className="fk-shell__hint fk-faint"><HotkeyKbd id="menu" /></span>
    </header>
  );
}
